import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  Slider,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import { LocationOn, NightsStay, Timeline, CheckCircle, AccessTime } from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  resolveProductLine,
  getStayLocationChoices,
  getStayStopLabels,
  isUmrahLine,
} from '../../../utils/productLineConfig';

const StayDurationStepV2 = ({ bookingData, updateBookingData }) => {
  const productLine = resolveProductLine(bookingData);
  const isUmrah = isUmrahLine(bookingData);
  const labels = getStayStopLabels(productLine);
  const locationChoices = getStayLocationChoices(productLine, bookingData.destinationCountry);

  const stayLocations = [
    {
      key: 'first',
      label: labels.first,
      locationKey: 'firstEntryLocation',
      nightsKey: 'firstEntryNights',
      includeKey: 'includeFirstEntry',
      defaultLocation: labels.defaults.first,
      accent: '#1F6F78',
    },
    {
      key: 'second',
      label: labels.second,
      locationKey: 'secondEntryLocation',
      nightsKey: 'secondEntryNights',
      includeKey: 'includeSecondEntry',
      defaultLocation: labels.defaults.second,
      accent: '#0E1C2A',
    },
    {
      key: 'third',
      label: labels.third,
      locationKey: 'thirdEntryLocation',
      nightsKey: 'thirdEntryNights',
      includeKey: 'includeThirdEntry',
      defaultLocation: labels.defaults.third,
      accent: '#5B7C8A',
    },
  ];

  const handleToggle = (includeKey) => {
    updateBookingData({ [includeKey]: !bookingData[includeKey] });
  };

  const handleNightsChange = (nightsKey, value) => {
    updateBookingData({ [nightsKey]: value });
  };

  const handleLocationChange = (locationKey, value) => {
    updateBookingData({ [locationKey]: value });
  };

  const totalNights = stayLocations.reduce((sum, stay) => {
    return bookingData[stay.includeKey] ? sum + (bookingData[stay.nightsKey] || 0) : sum;
  }, 0);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <NightsStay color="primary" />
        {isUmrah ? 'Stay & Duration Plan' : 'Destinations & Duration'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {productLine} — choose cities and nights for each stop.
      </Typography>

      <Grid container spacing={3}>
        {stayLocations.map((stay, index) => {
          const isIncluded = bookingData[stay.includeKey];
          const nightsValue = bookingData[stay.nightsKey];
          const locationValue = bookingData[stay.locationKey] || stay.defaultLocation;

          return (
            <Grid item xs={12} md={4} key={stay.key}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    height: '100%',
                    borderColor: isIncluded ? stay.accent : 'divider',
                    boxShadow: isIncluded ? '0 10px 30px rgba(31,111,120,0.12)' : 'none',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn sx={{ color: stay.accent }} />
                        <Typography variant="subtitle1" fontWeight={700}>
                          {stay.label}
                        </Typography>
                      </Box>
                      <Switch
                        checked={isIncluded}
                        onChange={() => handleToggle(stay.includeKey)}
                        inputProps={{ 'aria-label': `${stay.label} toggle` }}
                      />
                    </Box>

                    {isIncluded ? (
                      <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                          {locationChoices.map((choice) => (
                            <Chip
                              key={choice}
                              label={choice}
                              color={locationValue === choice ? 'primary' : 'default'}
                              variant={locationValue === choice ? 'filled' : 'outlined'}
                              onClick={() => handleLocationChange(stay.locationKey, choice)}
                            />
                          ))}
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Nights in {locationValue}
                        </Typography>
                        <Slider
                          value={nightsValue}
                          min={isUmrah ? 2 : 1}
                          max={14}
                          step={1}
                          valueLabelDisplay="auto"
                          onChange={(_, val) => handleNightsChange(stay.nightsKey, val)}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {nightsValue} night{nightsValue > 1 ? 's' : ''} planned in {locationValue}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {isUmrah
                          ? 'Toggle on to include this stop in your itinerary.'
                          : 'Toggle on to add another city to your trip.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>

      <Card
        variant="outlined"
        sx={{
          mt: 4,
          borderRadius: 2,
          p: 2,
          background: 'linear-gradient(135deg, rgba(31,111,120,0.06) 0%, rgba(14,28,42,0.04) 100%)',
        }}
      >
        <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timeline color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>
              {isUmrah ? 'Preferred Order' : 'Your Route'}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} divider={<Divider flexItem orientation="vertical" />} sx={{ width: '100%' }}>
            {stayLocations
              .filter((stay) => bookingData[stay.includeKey])
              .map((stay) => (
                <Box key={stay.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle fontSize="small" sx={{ color: stay.accent }} />
                  <Typography variant="body2" fontWeight={600}>
                    {bookingData[stay.locationKey] || stay.defaultLocation} — {bookingData[stay.nightsKey]} nights
                  </Typography>
                </Box>
              ))}
            {totalNights === 0 && (
              <Typography variant="body2" color="text.secondary">
                {isUmrah
                  ? 'Select at least one stay to build your route.'
                  : 'Select at least one destination to build your trip.'}
              </Typography>
            )}
          </Stack>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <AccessTime color="action" />
            <Typography variant="body2" fontWeight={700}>
              Total Nights: {totalNights}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StayDurationStepV2;
